import React, { useState, useEffect } from "react";
import { Box, Grid, IconButton, Tooltip } from "@mui/material";
import FlexBetween from "../../components/FlexBetween";
import DataTable from "../../components/DataGrids/DataGrid";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import { deleteSystem, reactivateSystem } from "../../services/systemService";
import useFetchPaginatedSystems from "../../hooks/useFetchPaginatedSystems";
import useDebounce from "../../hooks/useDebounce";
import { getFormattedDateTime } from "../../services/utils";
import {
  IconDeleteBlueSvg,
  IconEditBase,
  IconEyeBase,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
  IconReactivateSvg,
} from "../../common/utils/icons";
import { useToast } from "../../services/utils";
import Loader from "../../components/Molecules/Loader/Loader";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import { useAuth } from "../../contexts/AuthContext";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import ButtonComponent from "../../components/Button.Component";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import { useAppTranslation } from "../../hooks/useAppTranslation";

// Centralized Filter Configuration for System List
const SYSTEM_FILTER_CONFIG = {
  // Default filter state - single source of truth
  DEFAULT_STATE: {
    system_name: "",
    status_filter: "Active",
  },

  // Fields to persist in URL
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },

  // Field type mappings for URL serialization/deserialization
  FIELD_TYPES: {
    ID_FIELDS: [],
    STRING_FIELDS: ["system_name", "status_filter"],
    DATE_FIELDS: [],
  },
} as const;

const System: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const { t } = useAppTranslation();

  // Permission checks for system actions
  const canViewSystem = hasPermission("system", "view");
  const canAddSystem = hasPermission("system", "add");
  const canEditSystem = hasPermission("system", "edit");
  const canDeleteSystem = hasPermission("system", "delete");

  // pagination
  const defaultPage = process.env.REACT_APP_DEFAULT_PAGE;
  const { rowsPerPage } = useRowsPerPage();
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);

  // states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [systemToDelete, setSystemToDelete] = useState<any>(null);

  // SearchFilterBar state - initialize from URL parameters
  const [searchFilterData, setSearchFilterData] = useState<any>(() => {
    const initialState: any = {
      ...SYSTEM_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Restore filters from URL parameters
    SYSTEM_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        initialState[field] = urlValue;
      }
    });

    return initialState;
  });

  // Extract filters from searchFilterData
  const effectiveStatusFilter = searchFilterData?.status_filter || "";
  const effectiveSystemName = searchFilterData?.system_name || "";

  // Debounce system_name with 400ms delay
  const debouncedSystemName = useDebounce(effectiveSystemName);

  // Determine if we should include system_name in API call
  const effectiveSystemNameSearch =
    debouncedSystemName.trim().length >= 3 ? debouncedSystemName : "";

  // Convert status_filter to isActive parameter
  let isActiveParam = "true"; // Default to active
  if (effectiveStatusFilter === "Inactive") {
    isActiveParam = "false";
  } else if (effectiveStatusFilter === "All") {
    isActiveParam = "all";
  }

  // hooks
  const [fetchedSystems, refetchSystems] = useFetchPaginatedSystems({
    setIsLoading,
    page,
    pSize,
    searchTerm: effectiveSystemNameSearch,
    isActive: isActiveParam,
  });

  // Sync filters to URL parameters (state → URL)
  useEffect(() => {
    const newParams = new URLSearchParams();

    SYSTEM_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
      const value = searchFilterData[field];
      if (value !== null && value !== undefined && value !== "") {
        // Don't add default values to URL to keep it clean
        if (field === "status_filter" && value === "Active") {
          return; // Skip default status filter
        }
        newParams.set(field, String(value));
      }
    });

    // Only update URL if params have changed
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();
    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [searchFilterData, searchParams, setSearchParams]);

  // Update filter state when URL changes (URL → state for back/forward navigation)
  useEffect(() => {
    const urlFilterState: any = {
      ...SYSTEM_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Extract filter parameters from URL
    SYSTEM_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        urlFilterState[field] = urlValue;
      }
    });

    // Check if URL filter state differs from current filter state
    const hasFilterChanges = Object.keys(urlFilterState).some((key) => {
      const urlValue = urlFilterState[key];
      const currentValue = searchFilterData[key];

      // Handle null/undefined/empty string comparisons
      const normalizedUrlValue =
        urlValue === null || urlValue === undefined || urlValue === ""
          ? ""
          : urlValue;
      const normalizedCurrentValue =
        currentValue === null ||
        currentValue === undefined ||
        currentValue === ""
          ? ""
          : currentValue;

      return normalizedUrlValue !== normalizedCurrentValue;
    });

    // Only update state if there are actual changes to prevent infinite loops
    if (hasFilterChanges) {
      setSearchFilterData(urlFilterState);
    }
  }, [searchParams]); // Only depend on searchParams to detect URL changes

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [effectiveSystemNameSearch, effectiveStatusFilter]);

  const handelClickEvent = () => {
    navigate("/system/add");
  };

  // Handle delete system
  const handleDeleteSystem = (system: any) => {
    setSystemToDelete(system);
    setDeleteConfirmOpen(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!systemToDelete) return;

    try {
      setIsLoading(true);
      await deleteSystem(systemToDelete.id);
      showToast(t("systems.toast.deleted"), "success");
      setDeleteConfirmOpen(false);
      setSystemToDelete(null);
      // Refetch the systems list to update the UI
      refetchSystems();
    } catch (error: any) {
      showToast(error?.message || t("systems.toast.failedDelete"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle reactivate system
  const handleReactivate = async (system: any) => {
    try {
      setIsLoading(true);
      await reactivateSystem(system.id);
      showToast(t("systems.toast.reactivated"), "success");
      // Refetch the systems list to update the UI
      refetchSystems();
    } catch (error: any) {
      console.error("Error reactivating system:", error);
      showToast(
        error?.response?.data?.detail || t("systems.toast.failedReactivate"),
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      renderCell: () => <></>,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
    },
    {
      field: "system_id",
      headerName: t("systems.list.columns.systemId"),
      minWidth: 80,
      sortable: true,
      flex: 1,
      renderCell: (params: any) => {
        return <span>{params.id}</span>;
      },
    },
    {
      field: "value",
      headerName: t("systems.list.columns.systemName"),
      minWidth: 200,
      flex: 1,
      sortable: true,
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value} />;
      },
    },

    // {
    //   field: "is_active",
    //   headerName: "isActive",
    //   minWidth: 180,
    //   flex: 1,
    //   renderCell: (params: any) => {
    //     return params.value ? (
    //       <Box title="Active">
    //         <IconCheckCircleIconGreenSvg width={21} height={21} />
    //       </Box>
    //     ) : (
    //       <Box title="InActive">
    //         <IconCrossCircleIconSvg width={21} height={21} />
    //       </Box>
    //     );
    //   },
    // },

    {
      field: "updated_on",
      headerName: t("systems.list.columns.lastModified"),
      minWidth: 180,
      flex: 1,
      sortable: true,
      renderCell: (params: any) => {
        return getFormattedDateTime(params.value);
      },
    },
    {
      field: "is_active",
      headerName: t("systems.list.columns.status"),
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => {
        return params.value ? (
          <Tooltip title={t("common.status.active")} placement="top" arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCheckCircleIconGreenSvg width={21} height={21} />
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title={t("common.status.inactive")} placement="top" arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCrossCircleIconSvg width={21} height={21} />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "action",
      headerName: t("systems.list.columns.actions"),
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      width: 170,
      sortable: false,
      renderCell: (params: any) => {
        const isSystemActive = params.row.is_active === true;

        return (
          <>
            {/* For active records, show all action buttons */}
            {isSystemActive && (
              <>
                {canViewSystem && (
                  <Tooltip
                    title={t("systems.actions.view")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => {
                        navigate(`/system/view/${params.row.id}`);
                      }}
                    >
                      <IconEyeBase />
                    </IconButton>
                  </Tooltip>
                )}

                {canEditSystem && (
                  <Tooltip
                    title={t("systems.actions.edit")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => {
                        navigate(`/system/edit/${params.row.id}`);
                      }}
                    >
                      <IconEditBase />
                    </IconButton>
                  </Tooltip>
                )}

                {canDeleteSystem && (
                  <Tooltip
                    title={t("systems.actions.delete")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => handleDeleteSystem(params.row)}
                    >
                      <IconDeleteBlueSvg />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}

            {/* For inactive records, show ONLY Reactivate button */}
            {!isSystemActive && canEditSystem && (
              <Tooltip
                title={t("systems.actions.reactivate")}
                placement="top"
                arrow
              >
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() => handleReactivate(params.row)}
                >
                  <IconReactivateSvg />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  return (
    <Box>
      <Loader isLoading={isLoading} />

      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmDelete}
        dailogTitle={t("systems.dialog.deleteTitle")}
        dailogDescription={t("systems.dialog.deleteDescription", {
          name: systemToDelete?.value,
        })}
      />

      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              searchFilterData={searchFilterData}
              setSearchFilterData={setSearchFilterData}
              requiredFields={["system_name", "status_filter"]}
              primaryFields={["system_name", "status_filter"]}
              fieldRequirements={{
                defaultStatusFilter: "Active", // Reset to Active status on Clear Filter
              }}
            />
          </Grid>
          {canAddSystem && (
            <Grid
              item
              xs="auto"
              sx={{ display: "flex", alignItems: "flex-start" }}
            >
              <ButtonComponent
                handelClickEvent={handelClickEvent}
                className="btn-orange"
              >
                <>
                  <AddSharpIcon sx={{ marginRight: "4px" }} />
                  {t("systems.actions.add")}
                </>
              </ButtonComponent>
            </Grid>
          )}
        </Grid>
      </Box>

      <FlexBetween>
        <DataTable
          dataColumns={columns}
          dataRows={
            Array.isArray(fetchedSystems?.items) ? fetchedSystems.items : []
          }
          loading={isLoading}
          dataListTitle={t("systems.list.title")}
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          rowCount={fetchedSystems?.total || 0}
          paginationModel={{
            page: page - 1,
            pageSize: pSize,
          }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          singlePageMaxHeightDiff={368}
        />
      </FlexBetween>
    </Box>
  );
};

export default System;
